import get from 'lodash/get';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import {
  SAVE_PREFERENCES_COLLECTION,
  SAVE_PREFERENCES_DELIVERY,
  SAVE_PREFERENCES_PAYMENT_CARD,
  DELETE_PREFERENCES,
} from '../../../constants/actionConstants';
import {
  URL_PREFERENCES_COLLECTION,
  URL_PREFERENCES_DELIVERY,
  URL_PREFERENCES_PAYMENT_CARD,
  URL_PREFERENCES_DELETE,
} from '../../../constants/endpointConstants';
import paymentTypeConstants from '../../../constants/paymentTypeConstants';
import featureConstants from '../../../constants/featureConstants';
import userConstants from '../../../constants/userConstants';
import {
  SAVE_AS_DEFAULT_FORM_ID,
  SAVE_FULFILMENT_FORM_ID,
  SAVE_FULFILMENT_DELIVERY_FORM_ID,
  SAVE_FULFILMENT_COLLECTION_FORM_ID,
} from '../../../utils/form/configs/saveAsDefault';
import { isFeatureActive } from '../../reducers/config/configReducer';

export const deletePreferences = () => ({
  type: DELETE_PREFERENCES,
  request: client => client({ path: URL_PREFERENCES_DELETE, config: { method: 'DELETE' } }),
});

export const savePreferencesCollection = body => ({
  type: SAVE_PREFERENCES_COLLECTION,
  request: client => client({ path: URL_PREFERENCES_COLLECTION, config: { method: 'POST', body } }),
});

export const savePreferencesDelivery = body => ({
  type: SAVE_PREFERENCES_DELIVERY,
  request: client => client({ path: URL_PREFERENCES_DELIVERY, config: { method: 'POST', body } }),
});

export const savePreferencesCard = body => ({
  type: SAVE_PREFERENCES_PAYMENT_CARD,
  request: client => client({ path: URL_PREFERENCES_PAYMENT_CARD, config: { method: 'POST', body } }),
});

export function shouldSaveCardWithUpdatedAddress(state) {
  const selectedPaymentType = get(state, 'payment.selectedPaymentType');

  if (selectedPaymentType) {
    const parsedSelectedPaymentType = selectedPaymentType.split(':');
    const selectedPaymentTypeName = parsedSelectedPaymentType[0];
    const selectedPaymentCardId = parsedSelectedPaymentType[1];

    if (selectedPaymentTypeName === paymentTypeConstants.SAVED_CREDIT_CARD) {
      const savedCards = get(state, 'user.savedPaymentCards');
      const savedCardAddressId = find(savedCards, { id: selectedPaymentCardId }).addressId;
      const billingAddressId = get(state, 'user.selectedBillingAddress.id');

      const shouldSaveWithNewAddress = !isEqual(savedCardAddressId, billingAddressId);
      if (shouldSaveWithNewAddress) return { billingAddressId, selectedPaymentCardId };
    }
  }

  return {};
}

export function getPaymentCardBody(saveCardAsDefaultId, billingAddressId, selectedPaymentCardId) {
  return {
    cardId: saveCardAsDefaultId || selectedPaymentCardId,
    ...saveCardAsDefaultId && { default: true },
    ...billingAddressId && { addressId: billingAddressId },
  };
}

export function shouldSaveNonDefaultCollectionPoint(params) {
  const {
    savedCollectionPoints = [],
    confirmedCollectionPoint,
  } = params;

  if (!confirmedCollectionPoint) return null;

  const collectionPointAlreadySaved = !!savedCollectionPoints.find(point => point.id === confirmedCollectionPoint.id);
  return !collectionPointAlreadySaved;
}

export const handleDeliveryFulfilmentPreferences = () => (dispatch, getState) => {
  const saveFulfilmentForm = get(getState(), `form.${SAVE_FULFILMENT_FORM_ID}`);
  const fulfilmentChoiceDeliveryId = get(saveFulfilmentForm, `values.${SAVE_FULFILMENT_DELIVERY_FORM_ID}`);

  if (fulfilmentChoiceDeliveryId) {
    return dispatch(savePreferencesDelivery({
      addressId: fulfilmentChoiceDeliveryId,
      preferredFulfilmentOption: true,
      default: true,
    }));
  }

  if (saveFulfilmentForm) {
    const customerFulfilmentChoice = get(getState(), 'user.customerPreferences.fulfilmentChoice');
    if (customerFulfilmentChoice === userConstants.FULFILMENT_PREFERENCE_DELIVERY) {
      return dispatch(deletePreferences());
    }
  }

  return {};
};

export const handleCollectionFulfilmentPreferences = () => async (dispatch, getState) => {
  const saveFulfilmentForm = get(getState(), `form.${SAVE_FULFILMENT_FORM_ID}`);
  const saveCollectionPointAsDefaultId = get(saveFulfilmentForm, `values.${SAVE_FULFILMENT_COLLECTION_FORM_ID}`);

  if (saveCollectionPointAsDefaultId) {
    return dispatch(savePreferencesCollection({
      collectionPointId: saveCollectionPointAsDefaultId,
      preferredFulfilmentOption: true,
      default: true,
    }));
  }

  const savedCollectionPoints = getState().user?.collectionPoints;
  const confirmedCollectionPoint = getState().clickAndCollect?.confirmedCollectionPoint;

  if (shouldSaveNonDefaultCollectionPoint({ savedCollectionPoints, confirmedCollectionPoint })) {
    await dispatch(savePreferencesCollection({
      collectionPointId: confirmedCollectionPoint.id,
      default: false,
    }));

    const customerFulfilmentChoice = get(getState(), 'user.customerPreferences.fulfilmentChoice');
    if (customerFulfilmentChoice === userConstants.FULFILMENT_PREFERENCE_COLLECTION) {
      return dispatch(deletePreferences());
    }
  }

  return {};
};

export const saveDeliveryPreferences = () => (dispatch, getState) => {
  const fulfilmentPreferenceFeatureActive = isFeatureActive(getState(), featureConstants.ENABLE_FULFILMENT_PREFERENCE);

  if (fulfilmentPreferenceFeatureActive) {
    return dispatch(handleDeliveryFulfilmentPreferences());
  }

  const saveAddressAsDefaultId = get(getState(), 'addressBook.addressIdToBeSavedAsDefault');

  if (saveAddressAsDefaultId) {
    return dispatch(savePreferencesDelivery({
      addressId: saveAddressAsDefaultId,
      default: true,
    }));
  }

  return {};
};

export const saveCollectionPreferences = () => (dispatch, getState) => {
  const fulfilmentPreferenceFeatureActive = isFeatureActive(getState(), featureConstants.ENABLE_FULFILMENT_PREFERENCE);

  if (fulfilmentPreferenceFeatureActive) {
    return dispatch(handleCollectionFulfilmentPreferences());
  }

  const collectionPointId = get(getState(), `form.${SAVE_AS_DEFAULT_FORM_ID}.values.saveCollectionPointAsDefault`);

  if (collectionPointId) {
    return dispatch(savePreferencesCollection({
      collectionPointId,
      default: true,
    }));
  }

  const savedCollectionPoints = getState().user?.collectionPoints;
  const confirmedCollectionPoint = getState().clickAndCollect?.confirmedCollectionPoint;

  if (shouldSaveNonDefaultCollectionPoint({ savedCollectionPoints, confirmedCollectionPoint })) {
    return dispatch(savePreferencesCollection({
      collectionPointId: confirmedCollectionPoint.id,
      default: false,
    }));
  }

  return {};
};

export const saveCardPreferences = () => (dispatch, getState) => {
  // Save card as default or update saved card address
  const saveAsDefaultFormValues = get(getState(), `form.${SAVE_AS_DEFAULT_FORM_ID}.values`, {});
  const saveCardAsDefaultId = saveAsDefaultFormValues.savePaymentAsDefault;
  const { billingAddressId, selectedPaymentCardId } = shouldSaveCardWithUpdatedAddress(getState());
  const shouldUpdateCardAddress = !!billingAddressId && !!selectedPaymentCardId;

  if (saveCardAsDefaultId || shouldUpdateCardAddress) {
    const paymentCardBody = getPaymentCardBody(saveCardAsDefaultId, billingAddressId, selectedPaymentCardId);
    dispatch(savePreferencesCard(paymentCardBody));
  }
};
